'use client'

import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import classNames from 'classnames'
import Image from 'next/image'
import { useFormatter, useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import RoundButtonSmall from '@/components/Button/RoundButtonSmall'
import Label from '@/components/Label'
import { type NewsItem } from '@/lib/news'

import styles from './item.module.css'

interface Props {
  data: NewsItem
  unread?: boolean
}

// TODO: Define text styles in tailwind when we have defined styles from the designers
const Item = ({ data: { id, content, publishedAt, title, media, label }, unread }: Props): React.JSX.Element => {
  const format = useFormatter()
  const dateTime = new Date(publishedAt)
  const t = useTranslations()

  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null)
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null)

  const paginationRef = useRef<HTMLDivElement>(null)

  const date = format.dateTime(dateTime, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })

  return (
    <section className="grid grid-cols-12 md:gap-x" key={id}>
      <div className="relative col-span-12 md:col-span-3 xl:col-span-2">
        <div className="relative left-[-6px] md:sticky md:top-24">
          <div className="relative -left-8 flex items-center gap-12 py-8 md:absolute md:left-[-15px] md:top-24">
            <div
              className={classNames(
                'box-content h-[11px] w-[11px] shrink-0 rounded-full border-[10px] border-[#F7F7FA]',
                {
                  'bg-greyblue-70': unread == null || !unread,
                  'bg-primary': unread != null
                }
              )}
            />
            <Label>{t(`common.${label}`)}</Label>
          </div>
        </div>
      </div>
      <div className="col-span-12 mt-32 md:col-span-9 md:mt-0 xl:col-span-10">
        <time className="text-14" dateTime={dateTime.toISOString().slice(0, 10)}>
          {date}
        </time>
        <h2 className="text-b24 md:text-b36">{title}</h2>
        <div
          className="mt-16 overflow-hidden rounded-[12px] bg-white pb-20"
          style={{ boxShadow: '0 0 2px 0 rgba(0,0,0,0.10), 0 2px 4px 0 rgba(0,0,0,0.10)' }}
        >
          {media != null && (
            <div className={styles.swiper}>
              <Swiper
                modules={[Pagination, Navigation]}
                navigation={{ nextEl, prevEl }}
                pagination={{ clickable: true, el: paginationRef.current }}
              >
                {media.map(({ filename }, index) => (
                  <SwiperSlide key={index} style={{ width: '100%' }}>
                    <Image
                      alt=""
                      className="left-0 top-0 size-full object-cover"
                      height={494}
                      quality={96} // TODO: q=95 does not work! this is a bug and needs to be examined further
                      src={filename}
                      width={1020}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              {media.length > 1 && (
                <div className="relative h-48">
                  <div className="flex justify-center pt-20" ref={paginationRef} />
                  <div
                    className={classNames('absolute left-20 top-20', styles['navigation-button'])}
                    ref={ref => {
                      setPrevEl(ref)
                    }}
                  >
                    <RoundButtonSmall icon="ArrowSmallLeft" theme="light" />
                  </div>
                  <div
                    className={classNames('absolute right-20 top-20', styles['navigation-button'])}
                    ref={ref => {
                      setNextEl(ref)
                    }}
                  >
                    <RoundButtonSmall icon="ArrowSmallRight" theme="light" />
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            className={classNames('mx-20 mt-20 text-18', styles['news-item-text'])}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </div>
    </section>
  )
}

export default Item
